import { useEffect, useState } from "react";
import { styles, colors } from "../styles";

// Confirmation modal that requires the user to type a specific word before
// the confirm button enables. Built specifically to harden the soft-delete
// flow on service records — `window.confirm` defaults focus on OK, so an
// errant Enter (or a misplaced tap on a flex-1 row of Edit/Delete/Audit Trail
// buttons) was enough to nuke records. Typing "DELETE" is impossible by
// accident.
//
// Props:
//   open        — boolean; renders nothing when false
//   title       — short header
//   message     — body text (can include a preview of what's being deleted)
//   confirmWord — required typed word; defaults to "DELETE"
//   confirmLabel — button label (e.g. "Delete record", "Delete 5 records")
//   busy        — disables both buttons while the parent's write is in flight
//   onConfirm   — fires only when typed text exactly matches confirmWord
//   onCancel    — backdrop click / Esc / Cancel button
export default function TypedConfirmModal({
  open,
  title = "Confirm delete",
  message = "",
  confirmWord = "DELETE",
  confirmLabel = "Delete",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState("");

  // Clear the typed text whenever the modal closes so the next open starts
  // fresh — otherwise stale text would pre-arm the confirm button.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  // Esc cancels. Skipping the Enter shortcut on purpose — the whole point
  // of this modal is to neutralize accidental Enter taps.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  const matched = typed === confirmWord;

  return (
    <div
      onClick={() => !busy && onCancel?.()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "10px",
          padding: "20px",
          width: "100%",
          maxWidth: "380px",
          boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
        }}
      >
        <h3 style={{ margin: "0 0 10px", color: "#a00" }}>{title}</h3>
        {message && (
          <div
            style={{
              fontSize: "13px",
              color: "#333",
              whiteSpace: "pre-wrap",
              marginBottom: "12px",
            }}
          >
            {message}
          </div>
        )}
        <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>
          Type <strong>{confirmWord}</strong> to confirm.
        </div>
        <input
          autoFocus
          style={{
            ...styles.input,
            width: "100%",
            margin: "0 0 12px",
            boxSizing: "border-box",
          }}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={confirmWord}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            style={{
              ...styles.button,
              flex: 1,
              margin: 0,
              padding: "10px",
              color: colors.textMuted,
            }}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.button,
              flex: 1,
              margin: 0,
              padding: "10px",
              background: matched && !busy ? "#a00" : "#f3d6d6",
              color: matched && !busy ? "white" : "#999",
              cursor: matched && !busy ? "pointer" : "not-allowed",
              fontWeight: 600,
            }}
            onClick={onConfirm}
            disabled={!matched || busy}
          >
            {busy ? "Deleting..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
